/**
 * Budsjett — the year's budget against what actually came in and went out.  Money-pro G1 (owner 2026-10-31: «regnskap =
 * NOT bookkeeping, just budget vs actual»).
 *
 * Behind the `budget` capability (absent from the menu without it) and finance.budget (admin and the treasurer). The
 * mosque writes one line per category, income or cost; beside it the server puts the actual figure from what the platform
 * already holds (Vipps gifts, campaign gifts, cash gifts, the shop, approved and paid expense claims — core budget.js).
 * An actual with no line is still shown, «ikke budsjettert». Inline edit (the line's updatedAt goes along, so a change
 * made meanwhile is refused, not overwritten); removing takes two clicks (TwoStep). «Kopier fra i fjor» copies last
 * year's lines this year does not have. The CSV is for the accountant: budget, actual and difference per line.
 * Results and refusals are toasts (useMsg).
 */
import React, { useId, useState } from 'react';
import { download, errSummary, fieldErrors, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { isoToday } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { BarChart } from '../components/BarChart';
import { Budget, Expenses } from '../core';

const krText = (ore: number) => Expenses.kr(ore);
const signed = (ore: number) => (ore > 0 ? `+${krText(ore)}` : krText(ore));
const EMPTY = { kind: 'cost', category: '', amount: '', note: '' };
type Form = typeof EMPTY;

/** One kind's table: budget, actual, difference; a line edited in place. */
function KindTable({ kind, part, onChanged, say }: { kind: 'income' | 'cost'; part: Body; onChanged: () => void; say: (t: string, tone?: 'ok' | 'err') => void }) {
  const [edit, setEdit] = useState<{ id: string; category: string; amount: string; note: string; updatedAt: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const rows: Body[] = part.rows || [];
  const save = async () => {
    if (!edit || saving) return;
    const a = Budget.kroner(edit.amount);
    if (!a.ok) { setErrors({ amountOre: a.message }); return; }
    setSaving(true);
    try {
      const r = await patch(`/budget/${edit.id}`, { category: edit.category, amountOre: a.ore, note: edit.note.trim() || null, expectedUpdatedAt: edit.updatedAt });
      if (!r.ok) { setErrors(fieldErrors(r.body)); say(errSummary(r.body), 'err'); if (r.body.code === 'line_changed') { setEdit(null); onChanged(); } return; }
      setEdit(null); setErrors({}); say('Linjen er lagret.', 'ok'); onChanged();
    } finally { setSaving(false); }
  };
  const remove = async (id: string) => {
    const r = await send(`/budget/${id}`, { method: 'DELETE' });
    say(r.ok ? 'Linjen er fjernet.' : errSummary(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) onChanged();
  };
  const title = kind === 'income' ? 'Inntekter' : 'Kostnader';
  return (
    <>
      <h4>{title}</h4>
      <BarChart ariaLabel={`${title}: budsjett mot faktisk per kategori`} unit="kr" seriesLabels={['Budsjett', 'Faktisk']}
        series={rows.map((r) => ({ label: String(r.category).slice(0, 14), value: r.budgetOre / 100 }))}
        secondary={rows.map((r) => ({ label: r.category, value: r.actualOre / 100 }))} />
      <table className="t">
        <thead><tr><th scope="col">Kategori</th><th scope="col">Budsjett</th><th scope="col">Faktisk</th><th scope="col">Differanse</th><th scope="col">Merknad</th>
          <th scope="col"><span className="vh">Handling</span></th></tr></thead>
        <tbody>{rows.length ? rows.map((r) => (edit && edit.id === r.id ? (
          <tr key={r.id}>
            <td><input aria-label="Kategori" maxLength={Budget.MAX_CATEGORY} value={edit.category} onChange={(e) => { setErrors(dropChanged(errors, edit, { ...edit, category: e.target.value })); setEdit({ ...edit, category: e.target.value }); }} /><FieldError text={errors.category} /></td>
            <td><input aria-label="Budsjett i kroner" inputMode="decimal" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} /><FieldError text={errors.amountOre} /></td>
            <td>{krText(r.actualOre)}</td>
            <td>{signed(r.actualOre - (Budget.kroner(edit.amount).ok ? (Budget.kroner(edit.amount) as { ore: number }).ore : r.budgetOre))}</td>
            <td><input aria-label="Merknad" maxLength={Budget.MAX_NOTE} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /><FieldError text={errors.note} /></td>
            <td><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : 'Lagre'}</Btn> <Btn ghost onClick={() => { setEdit(null); setErrors({}); }}>Avbryt</Btn></td>
          </tr>
        ) : (
          <tr key={r.id || `x-${r.category}`}>
            <td><b>{r.category}</b>{!r.budgeted && <> <Tag tone="grey">ikke budsjettert</Tag></>}</td>
            <td>{krText(r.budgetOre)}</td>
            <td>{krText(r.actualOre)}</td>
            <td>{signed(r.differenceOre)}</td>
            <td className="sub">{r.note || ''}</td>
            <td>{r.id && <>
              <Btn ghost aria-label={`Endre ${r.category}`} onClick={() => { setErrors({}); setEdit({ id: r.id, category: r.category, amount: Expenses.formatKr(r.budgetOre, { grouped: false }), note: r.note || '', updatedAt: r.updatedAt }); }}>Endre</Btn>{' '}
              <TwoStep label="Fjern" ariaLabel={`Fjern ${r.category}`} confirmLabel="Klikk igjen for å fjerne" onConfirm={() => remove(r.id)} /></>}</td>
          </tr>
        ))) : <tr><td colSpan={6}>Ingen linjer ennå.</td></tr>}</tbody>
        <tfoot><tr><th scope="row">Sum</th><td>{krText(part.budgetOre)}</td><td>{krText(part.actualOre)}</td><td>{signed(part.differenceOre)}</td><td colSpan={2} /></tr></tfoot>
      </table>
    </>
  );
}

export function Budsjett() {
  const thisYear = Number(isoToday().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const { data, error, reload } = useLoad<Body>(`/budget?year=${year}`, [year]);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const yearId = useId();
  const listId = useId();
  const years = [...new Set([thisYear - 1, thisYear, thisYear + 1, ...((data && data.years) || [])])].sort();

  const add = async () => {
    if (!form || saving) return;
    const a = Budget.kroner(form.amount);
    if (!a.ok) { setErrors({ amountOre: a.message }); return; }
    setSaving(true);
    try {
      const r = await post('/budget', { year, kind: form.kind, category: form.category, amountOre: a.ore, note: form.note.trim() || null });
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); say('Linjen er lagt til.', 'ok'); reload();
    } finally { setSaving(false); }
  };
  const copy = async () => {
    const r = await post('/budget/copy', { year });
    say(r.ok ? r.body.message : errSummary(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) reload();
  };
  const exportCsv = async () => {
    const d = await download(`/v1/admin/budget/export.csv?year=${year}`, `budsjett-${year}.csv`);
    if (!d.ok) say(d.message || 'Kunne ikke hente CSV-filen.', 'err');
  };

  return (
    <Card title="Budsjett" hint="Årets budsjett mot det som faktisk kom inn og gikk ut: gaver, innsamlinger, kontantgaver, butikken og godkjente utlegg. Ikke regnskap — bare budsjett mot faktisk.">
      <div className="work-bar">
        <label htmlFor={yearId} className="vh">År</label>
        <select id={yearId} value={year} onChange={(e) => setYear(Number(e.target.value))}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
        <span className="sp" />
        {data && data.previousYearLines > 0 && <Btn ghost onClick={copy}>Kopier fra i fjor</Btn>}
        <Btn ghost onClick={exportCsv}>Last ned CSV</Btn>
        <Btn small={false} onClick={() => { setErrors({}); setForm({ ...EMPTY }); }}>+ Ny linje</Btn>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <CardBody>
          <table className="t">
            <tbody>
              <tr><th scope="row">Resultat, budsjett</th><td>{krText(data.result.budgetOre)}</td></tr>
              <tr><th scope="row">Resultat, faktisk</th><td>{krText(data.result.actualOre)}</td></tr>
            </tbody>
          </table>
          <KindTable kind="income" part={data.income} onChanged={reload} say={say} />
          <KindTable kind="cost" part={data.cost} onChanged={reload} say={say} />
          <p className="note">Faktisk: Vipps-gaver (trukket, minus refusjoner) og kontantgaver under «Gaver», eller «Innsamlinger» når de gjelder en innsamling;
            betalte butikkordrer under «Butikk»; godkjente og betalte utlegg under utleggets kategori. Differanse = faktisk − budsjett.</p>
        </CardBody>
      )}
      <FormDialog open={Boolean(form)} title={`Ny budsjettlinje ${year}`} dirty={Boolean(form) && JSON.stringify(form) !== JSON.stringify(EMPTY)} onClose={() => setForm(null)}>
        {form && <>
          <div className="row">
            <Field label="Type" required className="field half">{(id) => <select id={id} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {Budget.KINDS.map((k) => <option key={k} value={k}>{k === 'income' ? 'Inntekt' : 'Kostnad'}</option>)}</select>}</Field>
            <Field label="Kategori" required className="field half">{(id) => <><input id={id} list={listId} maxLength={Budget.MAX_CATEGORY} autoComplete="off" value={form.category}
              onChange={(e) => { setErrors(dropChanged(errors, form, { ...form, category: e.target.value })); setForm({ ...form, category: e.target.value }); }} />
              <datalist id={listId}>{(Budget.SUGGESTIONS[form.kind] || []).map((s) => <option key={s} value={s} />)}</datalist><FieldError text={errors.category} /></>}</Field>
          </div>
          <div className="row">
            <Field label="Beløp (kr)" required className="field half" note="For eksempel 12000 eller 12000,50.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="decimal" autoComplete="off" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} /><FieldError text={errors.amountOre} /></>}</Field>
            <Field label="Merknad" className="field half">{(id) => <><input id={id} maxLength={Budget.MAX_NOTE} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /><FieldError text={errors.note} /></>}</Field>
          </div>
          <div className="row"><Btn small={false} disabled={saving} onClick={add}>{saving ? 'Lagrer …' : 'Legg til'}</Btn></div>
        </>}
      </FormDialog>
    </Card>
  );
}
